import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Collapsible from '@radix-ui/react-collapsible';
import * as Select from '@radix-ui/react-select';
import { observer } from 'mobx-react-lite';
import { useFactTypesStore, usePersonFactsStore } from '@/hooks/useStores';
import type { CreatePersonFactRequest, UpdatePersonFactRequest } from 'we-tree-api-client/v2';
import type { PersonFactType } from '@/stores/models/PersonFact';
import CollapsibleChevron from '@/components/shared/CollapsibleChevron';
import Close from '@/icons/Close';
import Pencil from '@/icons/Pencil';
import Plus from '@/icons/Plus';
import CitationLinksField from '@/components/person/CitationLinksField';
import RecordInfoPopover from '@/components/person/RecordInfoPopover';

import ProfileSectionCount from '@/components/person/ProfileSectionCount';

import './person-facts.css';

interface PersonFactsProps {
  familyMemberId: string;
  familyId?: string;
  readonly?: boolean;
  defaultExpanded?: boolean;
  /** Member profile screen: section count pill and profile section chrome */
  variant?: 'default' | 'profile';
  /** Profile tab strip: flat panel without collapsible header */
  sectionMode?: 'collapsible' | 'tab-panel';
}

const PersonFacts: React.FC<PersonFactsProps> = observer(
  ({
    familyMemberId,
    familyId,
    readonly = false,
    defaultExpanded = true,
    variant = 'default',
    sectionMode = 'collapsible'
  }) => {
    const { t } = useTranslation('forms');
    const { t: tCommon } = useTranslation('common');
    const isProfileVariant = variant === 'profile';
    const isTabPanel = sectionMode === 'tab-panel';
    const factTypesStore = useFactTypesStore();
    const personFactsStore = usePersonFactsStore();
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);

    // Form state for adding/editing
    const [formData, setFormData] = useState<CreatePersonFactRequest>({
      familyMemberId,
      factTypeId: '',
      detail: '',
      notes: '',
      sourceNotes: ''
    });

    // Load fact types from store (only active ones)
    useEffect(() => {
      // Only load if not already loaded or if last loaded was more than 5 minutes ago
      const shouldLoad =
        factTypesStore.factTypes.size === 0 ||
        !factTypesStore.lastLoadedAt ||
        Date.now() - factTypesStore.lastLoadedAt.getTime() > 5 * 60 * 1000;

      if (shouldLoad) {
        // Load all fact types (not just active ones) so we can show system fact types too
        // The activeFactTypes view will filter to show active OR system fact types
        factTypesStore.loadFactTypes(undefined);
      }
    }, [factTypesStore]);

    // Load person facts from store
    useEffect(() => {
      personFactsStore.loadFactsForFamilyMember(familyMemberId);
    }, [familyMemberId, personFactsStore]);

    const handleAdd = () => {
      setFormData({
        familyMemberId,
        factTypeId: '',
        detail: '',
        notes: '',
        sourceNotes: ''
      });
      setIsAdding(true);
      setEditingId(null);
    };

    const handleEdit = (factId: string) => {
      const fact = personFactsStore.getFactById(factId);
      if (!fact) return;

      setFormData({
        familyMemberId,
        factTypeId: fact.factTypeId,
        detail: fact.detail || '',
        notes: fact.notes || '',
        sourceNotes: fact.sourceNotes || ''
      });
      setEditingId(fact.id);
      setIsAdding(false);
    };

    const handleCancel = () => {
      setIsAdding(false);
      setEditingId(null);
      setFormData({
        familyMemberId,
        factTypeId: '',
        detail: '',
        notes: '',
        sourceNotes: ''
      });
    };

    const handleSave = async () => {
      if (!formData.factTypeId) {
        personFactsStore.setError(familyMemberId, t('errors.selectFactType'));
        return;
      }

      if (editingId) {
        // Update existing fact
        const updateData: UpdatePersonFactRequest = {
          factTypeId: formData.factTypeId,
          detail: formData.detail || undefined,
          notes: formData.notes || undefined,
          sourceNotes: formData.sourceNotes || undefined
        };
        const result = await personFactsStore.updatePersonFact(editingId, updateData);
        if (result.success) {
          handleCancel();
        } else {
          personFactsStore.setError(familyMemberId, result.error?.getUserMessage() || t('errors.updateFactFailed'));
        }
      } else {
        // Create new fact
        const result = await personFactsStore.createPersonFact(formData);
        if (result.success) {
          handleCancel();
        } else {
          personFactsStore.setError(familyMemberId, result.error?.getUserMessage() || t('errors.createFactFailed'));
        }
      }
    };

    const handleDelete = async (factId: string) => {
      if (!confirm(t('confirm.deleteFact'))) {
        return;
      }

      const result = await personFactsStore.deletePersonFact(factId);
      if (!result.success) {
        personFactsStore.setError(familyMemberId, result.error?.getUserMessage() || t('errors.deleteFactFailed'));
      }
    };

    // Get active fact types from store
    const activeFactTypes = factTypesStore.activeFactTypes;

    // Get facts for this family member from store, sorted by fact type display name
    const facts = personFactsStore.getFactsForFamilyMember(familyMemberId);
    const sortedFacts = [...facts].sort((a, b) => {
      const nameA = factTypesStore.getFactTypeById(a.factTypeId)?.displayName ?? '';
      const nameB = factTypesStore.getFactTypeById(b.factTypeId)?.displayName ?? '';
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });

    // Get loading and error states from stores
    const isLoading = personFactsStore.isLoadingForFamilyMember(familyMemberId) || factTypesStore.isLoading;
    const displayError = personFactsStore.getErrorForFamilyMember(familyMemberId) || factTypesStore.error || null;

    // Show loading state if facts are loading or fact types are loading
    if (isLoading) {
      return (
        <div className="person-facts">
          <div className="person-facts__loading">{t('person.loadingFacts')}</div>
        </div>
      );
    }

    const getFactTypeDisplayName = (factTypeId: string): string => {
      const factType = factTypesStore.getFactTypeById(factTypeId);
      return factType?.displayName || tCommon('unknown');
    };

    const getFactTypeDescription = (factTypeId: string): string | null => {
      const factType = factTypesStore.getFactTypeById(factTypeId);
      return factType?.description || null;
    };

    const addButton = !readonly ? (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          handleAdd();
        }}
        className={isTabPanel ? 'btn btn--ghost btn--sm person-facts__add-button' : 'person-facts__add-button'}
        disabled={isAdding || editingId !== null}
      >
        {isProfileVariant || isTabPanel ? (
          <>
            <Plus width={13} height={13} />
            {t('person.addFact')}
          </>
        ) : (
          t('person.addFactPlus')
        )}
      </button>
    ) : null;

    const sectionBody = (
      <div className="person-facts__body">
        {displayError && (
          <div className="person-facts__error" role="alert">
            <span>{displayError}</span>
            <button
              type="button"
              onClick={() => {
                personFactsStore.setError(familyMemberId, null);
                factTypesStore.setError(null);
              }}
              className="person-facts__error-dismiss"
              aria-label={t('actions.dismiss')}
            >
              ×
            </button>
          </div>
        )}

        {(isAdding || editingId) && (
          <div className="person-facts__form">
            <h3 className="person-facts__form-title">
              {editingId ? t('person.editFactTitle') : t('person.addFactTitle')}
            </h3>
            <div className="person-facts__form-row person-facts__form-row--type-detail">
              <div className="person-facts__form-field person-facts__form-field--type">
                <label className="person-facts__form-label">
                  {t('person.factType')} <span className="person-facts__required">*</span>
                </label>
                <Select.Root
                  value={formData.factTypeId || ''}
                  onValueChange={(v) => setFormData({ ...formData, factTypeId: v })}
                  disabled={!!editingId}
                >
                  <Select.Trigger className="form-select-trigger" aria-label={t('person.factType')}>
                    <Select.Value placeholder={t('person.selectFactTypePlaceholder')} />
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                      {activeFactTypes.map((ft) => (
                        <Select.Item key={ft.id} value={ft.id} className="form-select-item">
                          <Select.ItemText>{ft.displayName}</Select.ItemText>
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Portal>
                </Select.Root>
              </div>
              <div className="person-facts__form-field person-facts__form-field--detail">
                <label className="person-facts__form-label">{t('person.detail')}</label>
                <input
                  type="text"
                  value={formData.detail}
                  onChange={(e) => setFormData({ ...formData, detail: e.target.value })}
                  className="person-facts__form-input"
                  placeholder={t('person.factDetailPlaceholder')}
                />
              </div>
            </div>
            {formData.factTypeId && getFactTypeDescription(formData.factTypeId) && (
              <p className="person-facts__form-type-description">{getFactTypeDescription(formData.factTypeId)}</p>
            )}
            <div className="person-facts__form-field">
              <label className="person-facts__form-label">{t('person.notes')}</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="person-facts__form-textarea"
                placeholder={t('person.notesPlaceholder')}
                rows={3}
              />
            </div>
            <div className="person-facts__form-field">
              <label className="person-facts__form-label">{t('person.sourceNotes')}</label>
              <textarea
                value={formData.sourceNotes}
                onChange={(e) => setFormData({ ...formData, sourceNotes: e.target.value })}
                className="person-facts__form-textarea"
                placeholder={t('person.sourceNotesPlaceholder')}
                rows={2}
              />
            </div>
            {familyId && editingId && (
              <CitationLinksField
                familyId={familyId}
                targetType="person_fact"
                targetId={editingId}
                readonly={readonly}
              />
            )}
            <div className="person-facts__form-actions">
              <button type="button" onClick={handleCancel} className="person-facts__cancel-button">
                {tCommon('cancel')}
              </button>
              <button type="button" onClick={handleSave} className="person-facts__save-button">
                {editingId ? t('actions.save') : tCommon('add')}
              </button>
            </div>
          </div>
        )}

        {sortedFacts.length === 0 && !isAdding && (
          <div className="person-facts__empty">
            {readonly ? t('person.emptyFactsReadonly') : t('person.emptyFacts')}
          </div>
        )}

        {sortedFacts.length > 0 && (
          <div className="person-facts__list">
            {sortedFacts.map((fact: PersonFactType) => (
              <div
                key={fact.id}
                className={isProfileVariant ? 'person-facts__item person-facts__item--profile' : 'person-facts__item'}
              >
                {editingId === fact.id ? null : (
                  <div
                    className={
                      isProfileVariant
                        ? 'person-facts__item-row person-facts__item-row--profile'
                        : 'person-facts__item-row'
                    }
                  >
                    {isProfileVariant ? (
                      <>
                        <div className="person-facts__item-type">{getFactTypeDisplayName(fact.factTypeId)}</div>
                        <div className="person-facts__item-detail-wrap">
                          {fact.detail ? <div className="person-facts__item-detail">{fact.detail}</div> : null}
                        </div>
                        {familyId && (
                          <div className="person-facts__item-meta">
                            <div className="profile-entry-actions">
                              <RecordInfoPopover
                                createdAt={fact.createdAt}
                                updatedAt={fact.updatedAt}
                                familyId={familyId}
                                targetType="person_fact"
                                targetId={fact.id}
                                notes={fact.notes}
                                sourceNotes={fact.sourceNotes}
                                createdByNickname={fact.createdByNickname}
                                updatedByNickname={fact.updatedByNickname}
                                triggerClassName="profile-entry-icon-btn record-info-popover__trigger"
                                ariaLabel={t('person.recordInfo')}
                              />
                              {!readonly && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleEdit(fact.id)}
                                    className="profile-entry-icon-btn"
                                    disabled={isAdding || editingId !== null}
                                    aria-label={t('person.editEventAria', {
                                      name: getFactTypeDisplayName(fact.factTypeId)
                                    })}
                                    title={t('person.editFact')}
                                  >
                                    <Pencil width={13} height={13} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDelete(fact.id)}
                                    className="profile-entry-icon-btn profile-entry-icon-btn--danger"
                                    disabled={isAdding || editingId !== null}
                                    aria-label={t('person.deleteEventAria', {
                                      name: getFactTypeDisplayName(fact.factTypeId)
                                    })}
                                    title={t('person.deleteFact')}
                                  >
                                    <Close width={13} height={13} />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="person-facts__item-content">
                          <div className="person-facts__item-type-wrapper">
                            <span className="person-facts__item-type">{getFactTypeDisplayName(fact.factTypeId)}</span>
                          </div>
                          {fact.detail && (
                            <>
                              <span className="person-facts__item-separator">:</span>
                              <span className="person-facts__item-detail">{fact.detail}</span>
                            </>
                          )}
                          {familyId && (
                            <RecordInfoPopover
                              createdAt={fact.createdAt}
                              updatedAt={fact.updatedAt}
                              familyId={familyId}
                              targetType="person_fact"
                              targetId={fact.id}
                              notes={fact.notes}
                              sourceNotes={fact.sourceNotes}
                              createdByNickname={fact.createdByNickname}
                              updatedByNickname={fact.updatedByNickname}
                              triggerClassName="person-facts__record-info-trigger record-info-popover__trigger"
                              ariaLabel={t('person.recordInfo')}
                            />
                          )}
                        </div>
                        {!readonly && (
                          <div className="person-facts__item-actions">
                            <button
                              type="button"
                              onClick={() => handleEdit(fact.id)}
                              className="person-facts__edit-button"
                              disabled={isAdding || editingId !== null}
                            >
                              {tCommon('edit')}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(fact.id)}
                              className="person-facts__delete-button"
                              disabled={isAdding || editingId !== null}
                            >
                              {tCommon('delete')}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );

    if (isTabPanel) {
      return (
        <div className="person-facts person-facts--profile person-facts--tab-panel">
          <div className="tab-panel-card__head">
            <h2 className="tab-panel-card__title">{t('person.tabs.facts')}</h2>
            {addButton}
          </div>
          <div className="tab-panel-card__body">{sectionBody}</div>
        </div>
      );
    }

    return (
      <Collapsible.Root
        open={expanded}
        onOpenChange={setExpanded}
        className={isProfileVariant ? 'person-facts person-facts--profile' : 'person-facts'}
      >
        <div className="person-facts__header">
          <Collapsible.Trigger asChild>
            <button type="button" className="person-facts__header-button">
              <CollapsibleChevron className="person-facts__chevron" />
              <h2 className="person-facts__title">
                {t('person.tabs.facts')}
                {isProfileVariant && <ProfileSectionCount count={sortedFacts.length} />}
              </h2>
            </button>
          </Collapsible.Trigger>
          {addButton}
        </div>

        <Collapsible.Content>{sectionBody}</Collapsible.Content>
      </Collapsible.Root>
    );
  }
);

export default PersonFacts;
